import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
    House,
    ShoppingCart,
    Package,
    Users,
    MapPin,
    ChartBar,
    Clock,
    SignOut,
    List,
    Gear,
    TextAa,
    ShieldCheck,
    CheckSquare,
    Buildings,
    CurrencyDollar,
    ListMagnifyingGlass,
    Receipt,
    Printer,
    CreditCard,
    AddressBook,
    ChartLineUp
} from '@phosphor-icons/react';

const ExploremosLogo = ({ className = '', dark = false }) => (
    <svg viewBox="0 0 800 375" className={className} aria-label="Exploremos" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M 165 245 Q 400 110 635 245"
            fill="none"
            stroke="#FB7185"
            strokeWidth="10"
            strokeLinecap="round"
        />
        <text
            x="400"
            y="340"
            textAnchor="middle"
            fontFamily="'Inter','Helvetica Neue',Arial,sans-serif"
            fontSize="92"
            fontWeight="700"
            fill={dark ? '#0F1B3D' : '#FFFFFF'}
            letterSpacing="-2.5"
        >
            exploremos
        </text>
    </svg>
);

const Sidebar = ({ isOpen, onClose, onOpenLocationSwitcher }) => {
    const { user, logout, can, canAny, isAdmin } = useAuth();
    const isSeller = user?.role === 'seller';
    const canPickLocation = user?.role !== 'admin' && can('pos.access');
    const navigate = useNavigate();
    const [currentLocationName, setCurrentLocationName] = useState('');

    // Resolve the friendly name of the seller's "current_location_id" so it
    // can be shown next to the Ubicación switcher button. Refetches whenever
    // the user doc changes (i.e. after switching via the modal).
    useEffect(() => {
        if (!canPickLocation || !user?.current_location_id) {
            setCurrentLocationName('');
            return;
        }
        const api = `${process.env.REACT_APP_BACKEND_URL}/api`;
        axios.get(`${api}/locations`)
            .then(({ data }) => {
                const loc = (data || []).find(l => l.id === user.current_location_id);
                setCurrentLocationName(loc?.name || '');
            })
            .catch(() => setCurrentLocationName(''));
    }, [user?.current_location_id, canPickLocation]);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    // Permission-driven nav: each item declares the permission required.
    // Some items are visible if user has ANY of several permissions (read_all OR read_own).
    const navGroups = [
        {
            label: 'Principal',
            items: [
                { to: '/', icon: House, label: 'Dashboard',
                  show: () => can('dashboard.view') || canAny('reports.dashboard_global', 'reports.dashboard_own') },
                { to: '/pos', icon: ShoppingCart, label: isSeller ? 'Punto de Activación' : 'Punto de Venta',
                  show: () => can('pos.access') },
                { to: '/my-sales', icon: List, label: isSeller ? 'Mis Activaciones' : 'Mis Ventas',
                  show: () => can('sales.read_own') && !can('sales.read_all') },
                { to: '/sales', icon: List, label: 'Ventas',
                  show: () => can('sales.read_all') },
                { to: '/customer-directory', icon: AddressBook, label: 'Directorio',
                  show: () => can('customers.read_all') || can('customers.read_limited') },
            ]
        },
        {
            label: 'Caja',
            items: [
                { to: '/my-commissions', icon: Receipt, label: 'Mis Comisiones',
                  show: () => can('commissions.read_own') && !can('commissions.read_all') },
                { to: '/commissions', icon: CurrencyDollar, label: 'Comisiones',
                  show: () => can('commissions.read_all') },
                { to: '/payments-validation', icon: CheckSquare, label: 'Validación de Pagos',
                  show: () => can('payments.validate_view') },
                { to: '/cash-cuts-audit', icon: ListMagnifyingGlass, label: 'Auditoría de Cortes',
                  show: () => isAdmin() || can('cash_cuts.audit_detail') },
            ]
        },
        {
            label: 'Catálogo',
            items: [
                { to: '/products', icon: Package, label: 'Productos',
                  show: () => can('products.read') && (can('products.create') || can('products.update') || isAdmin()) },
                { to: '/templates', icon: TextAa, label: 'Plantillas',
                  show: () => can('templates.read') },
            ]
        },
        {
            label: 'Operación',
            items: [
                { to: '/attendance', icon: Clock, label: 'Asistencia',
                  show: () => can('attendance.read_all') },
                { to: '/reports', icon: ChartBar, label: 'Reportes',
                  show: () => can('reports.sales_report') },
                { to: '/productivity', icon: ChartLineUp, label: 'Productividad',
                  show: () => canAny('reports.dashboard_global', 'reports.export') },
                { to: '/users', icon: Users, label: 'Usuarios',
                  show: () => can('users.read_all') },
                { to: '/locations', icon: MapPin, label: 'Sucursales',
                  show: () => can('locations.read') && (can('locations.create') || can('locations.update') || isAdmin()) },
            ]
        },
        {
            label: 'Configuración',
            items: [
                { to: '/roles', icon: ShieldCheck, label: 'Roles',
                  show: () => can('roles.read') || isAdmin() },
                { to: '/company', icon: Buildings, label: 'Empresa',
                  show: () => can('company.update') },
                { to: '/payment-terminals', icon: CreditCard, label: 'TPVs',
                  show: () => can('company.payment_terminals') },
                { to: '/receipt-config', icon: Receipt, label: 'Recibo',
                  show: () => can('company.receipt_config') },
                { to: '/printer-setup', icon: Printer, label: 'Impresora',
                  show: () => can('company.receipt_config') },
                { to: '/settings', icon: Gear, label: 'Conectividad',
                  show: () => can('messaging.settings_view') },
            ]
        }
    ];

    const filteredGroups = navGroups
        .map(g => ({ ...g, items: g.items.filter(i => {
            try { return i.show(); } catch { return false; }
        }) }))
        .filter(g => g.items.length > 0);

    const userInitial = user?.name?.charAt(0).toUpperCase() || 'U';
    const roleLabel = {
        admin: 'Administrador',
        manager: 'Gerente',
        seller: 'Promotora',
        customer_service: 'Atención a Clientes',
        accounting_assistant: 'Auxiliar Administrativo'
    }[user?.role] || user?.role;

    return (
        <>
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden"
                    onClick={onClose}
                    data-testid="sidebar-overlay"
                />
            )}

            <aside
                className={`
                    fixed lg:static inset-y-0 left-0 z-50
                    w-64 flex flex-col
                    transform transition-transform duration-300 ease-out
                    ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
                `}
                style={{ backgroundColor: 'var(--sidebar-bg)', color: 'var(--sidebar-fg)' }}
                data-testid="sidebar"
            >
                {/* Brand */}
                <div className="px-5 pt-7 pb-6" style={{ borderBottom: '1px solid var(--sidebar-border)' }}>
                    <ExploremosLogo className="h-28 w-auto" />
                    <p className="mt-3 text-[11px] tracking-wider uppercase" style={{ color: 'var(--sidebar-fg-muted)' }}>
                        Point of Sale
                    </p>
                </div>

                {/* Navigation */}
                <nav className="flex-1 overflow-y-auto sidebar-scroll px-3 py-5 space-y-6">
                    {filteredGroups.map((group) => (
                        <div key={group.label}>
                            <div
                                className="px-3 mb-2 text-[10.5px] font-semibold uppercase"
                                style={{ color: 'var(--sidebar-fg-label)', letterSpacing: '0.1em' }}
                            >
                                {group.label}
                            </div>
                            <div className="space-y-0.5">
                                {group.items.map((item) => (
                                    <NavLink
                                        key={item.to}
                                        to={item.to}
                                        end={item.to === '/'}
                                        onClick={onClose}
                                        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                                        className={({ isActive }) => `
                                            group relative flex items-center gap-2.5 px-3 py-2 rounded-md
                                            text-[13.5px] transition-all duration-150
                                            ${isActive ? 'sidebar-nav-active' : 'sidebar-nav-idle'}
                                        `}
                                        style={({ isActive }) => ({
                                            color: isActive ? '#FFFFFF' : 'var(--sidebar-fg-muted)',
                                            backgroundColor: isActive ? 'var(--sidebar-active)' : 'transparent',
                                            fontWeight: isActive ? 500 : 400
                                        })}
                                    >
                                        {({ isActive }) => (
                                            <>
                                                {isActive && (
                                                    <span
                                                        className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r"
                                                        style={{ backgroundColor: 'var(--brand-coral)' }}
                                                    />
                                                )}
                                                <item.icon size={16} weight={isActive ? 'fill' : 'regular'} />
                                                <span>{item.label}</span>
                                            </>
                                        )}
                                    </NavLink>
                                ))}
                            </div>
                        </div>
                    ))}
                </nav>

                {/* User card */}
                <div className="p-3" style={{ borderTop: '1px solid var(--sidebar-border)' }}>
                    {canPickLocation && onOpenLocationSwitcher && (
                        <button
                            type="button"
                            onClick={() => { onOpenLocationSwitcher(); onClose?.(); }}
                            data-testid="sidebar-location-switcher"
                            className="w-full group flex items-center gap-2.5 px-3 py-2 mb-2 rounded-md text-[13.5px] transition-colors hover:bg-white/5"
                            style={{ color: 'var(--sidebar-fg-muted)' }}
                            title="Cambiar mi ubicación de trabajo"
                        >
                            <MapPin size={16} weight="duotone" className="text-[#FB7185]" />
                            <span className="flex-1 text-left truncate">
                                Ubicación
                            </span>
                            <span className="text-[10.5px] uppercase tracking-wider opacity-70 truncate max-w-[90px]">
                                {currentLocationName || 'Sin ubicación'}
                            </span>
                        </button>
                    )}
                    <div
                        className="group flex items-center gap-2.5 px-2 py-2 rounded-md cursor-pointer transition-colors"
                        style={{ backgroundColor: 'transparent' }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--sidebar-hover)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        data-testid="sidebar-user-card"
                    >
                        <div
                            className="w-8 h-8 rounded-md flex items-center justify-center text-xs font-semibold text-white flex-shrink-0"
                            style={{ backgroundColor: 'var(--brand-navy-light)', border: '1px solid rgba(255,255,255,0.1)' }}
                        >
                            {userInitial}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium text-white truncate">{user?.name}</div>
                            <div className="text-[10.5px] truncate" style={{ color: 'var(--sidebar-fg-muted)' }}>
                                {roleLabel}
                            </div>
                        </div>
                        <button
                            onClick={handleLogout}
                            data-testid="logout-btn"
                            className="p-1 rounded hover:bg-white/10 transition-colors"
                            style={{ color: 'var(--sidebar-fg-muted)' }}
                            title="Cerrar sesión"
                        >
                            <SignOut size={14} />
                        </button>
                    </div>
                </div>
            </aside>
        </>
    );
};

export default Sidebar;
